// src/components/Signup.jsx
import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Card, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/images/unionIcon.svg';
import { supabaseDataService } from '../services/supabaseService';
import _ from "lodash";
import {removeDots} from "@/utils/validation.js";

const Signup = (subdomainObj) => {
    // Account credentials
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [betacode, setBetacode] = useState('');

    // Personal information
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');

    // Address information
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [country, setCountry] = useState('United States');

    const [subdomain, setSubDomain] = useState(_.get(subdomainObj,'subdomain', '') || '');
    const [selectedGroup, setSelectedGroup] = useState( _.get(subdomainObj,'subdomain', '') || '' );

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [emailValidation, setEmailValidation] = useState({
        isValidating: false,
        isValid: null,
        message: ''
    });

    const [groupValidation, setGroupValidation] = useState({
        isValidating: false,
        isValid: null,
        message: ''
    });

    const [betacodeValidation, setBetacodeValidation] = useState({
        isValidating: false,
        isValid: null,
        message: ''
    });

    const navigate = useNavigate();
    const { signup, checkEmailExists } = useAuth();

    useEffect(() => {

    }, []);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (email && /\S+@\S+\.\S+/.test(email)) {
                validateEmail(email);
            } else if (email) {
                setEmailValidation({
                    isValidating: false,
                    isValid: false,
                    message: 'Please enter a valid email address'
                });
            } else {
                setEmailValidation({
                    isValidating: false,
                    isValid: null,
                    message: ''
                });
            }
        }, 500); // 500ms delay

        return () => clearTimeout(debounceTimer);
    }, [email]);

    useEffect(() => {
        const debounceTimer = setTimeout(async () => {
            if ( subdomain ){
                setGroupValidation({
                    isValidating: false,
                    isValid: true,
                    message: ''
                });

            }else if ( selectedGroup && selectedGroup.length >= 4){
                validateGroup(selectedGroup);
            }else if (selectedGroup && selectedGroup.length < 4){
                setGroupValidation({
                    isValidating: false,
                    isValid: false,
                    message: 'group name must be at least 4 characters'
                });
            }
        }, 500); // 500ms delay

        return () => clearTimeout(debounceTimer);
    }, [selectedGroup]);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (betacode && betacode.length >= 6) {
                validateBetacode(betacode);
            } else if (betacode && betacode.length < 6) {
                setBetacodeValidation({
                    isValidating: false,
                    isValid: false,
                    message: 'Beta code must be at least 6 characters long'
                });
            } else {
                setBetacodeValidation({
                    isValidating: false,
                    isValid: null,
                    message: ''
                });
            }
        }, 500); // 500ms delay

        return () => clearTimeout(debounceTimer);
    }, [betacode]);

    const validateEmail = async (emailToValidate) => {
        setEmailValidation({
            isValidating: true,
            isValid: null,
            message: 'Checking email availability...'
        });

        try {
            const exists = await checkEmailExists(emailToValidate);

            if (exists) {
                setEmailValidation({
                    isValidating: false,
                    isValid: false,
                    message: 'This email is already registered. Please use a different email or try logging in.'
                });
            } else {
                setEmailValidation({
                    isValidating: false,
                    isValid: true,
                    message: 'Email is available'
                });
            }
        } catch (error) {
            console.error('Email validation error:', error);
            setEmailValidation({
                isValidating: false,
                isValid: null,
                message: 'Unable to verify email availability. Please try again.'
            });
        }
    };

    const validateGroup = async (groupToValidate) => {
        setGroupValidation({
            isValidating: true,
            isValid: null,
            message: 'Checking group availability...'
        });

        try {
            const exists = await supabaseDataService.getGroupByName(selectedGroup)

            if (exists && !_.isEmpty(exists)) {
                setGroupValidation({
                    isValidating: false,
                    isValid: false,
                    message: 'This group is already registered. Please use a different group name.'
                });
            } else {
                setGroupValidation({
                    isValidating: false,
                    isValid: true,
                    message: 'Group is available'
                });
            }
        } catch (error) {
            console.error('Group validation error:', error);
            setEmailValidation({
                isValidating: false,
                isValid: null,
                message: 'Unable to verify Group availability. Please try again.'
            });
        }
    };

    const validateBetacode = async (betacodeToValidate) => {
        setBetacodeValidation({
            isValidating: true,
            isValid: null,
            message: 'Validating beta code...'
        });

        try {
            // Beta code validation logic
            // You can customize this based on your requirements
            const isValidFormat = /^[A-Za-z0-9]{6,20}$/.test(betacodeToValidate);

            if (!isValidFormat) {
                setBetacodeValidation({
                    isValidating: false,
                    isValid: false,
                    message: 'Beta code must be 6-20 alphanumeric characters'
                });
                return;
            }

            const isValidBetacode = await checkBetacodeExists(betacodeToValidate);

            if (isValidBetacode) {
                setBetacodeValidation({
                    isValidating: false,
                    isValid: true,
                    message: 'Valid beta code'
                });
            } else {
                setBetacodeValidation({
                    isValidating: false,
                    isValid: false,
                    message: 'Invalid beta code. Please check and try again.'
                });
            }
        } catch (error) {
            console.error('Beta code validation error:', error);
            setBetacodeValidation({
                isValidating: false,
                isValid: null,
                message: 'Unable to verify beta code. Please try again.'
            });
        }
    };

    const checkBetacodeExists = async (code) => {
        await new Promise(resolve => setTimeout(resolve, 50));

        // Mock validation - replace with actual API call
        // For demonstration, let's say valid codes start with "BETA"
        return code.toUpperCase().startsWith('987654321');
    };

    const formatPhoneNumber = (value) => {
        // Remove all non-digit characters
        const phoneNumber = value.replace(/\D/g, '');

        // Format as (XXX) XXX-XXXX
        if (phoneNumber.length <= 3) {
            return phoneNumber;
        } else if (phoneNumber.length <= 6) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        } else {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
        }
    };

    const handlePhoneChange = (e) => {
        const input = e.target.value;
        const formatted = formatPhoneNumber(input);
        setPhone(formatted);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email || !password || !firstName || !lastName || !betacode) {
            return setError('Please fill in all required fields');
        }

        // Check if email validation is still in progress
        if (emailValidation.isValidating) {
            return setError('Please wait for email validation to complete');
        }

        // Check if email is invalid or already exists
        if (emailValidation.isValid === false) {
            return setError(emailValidation.message);
        }

        // Check if betacode validation is still in progress
        if (betacodeValidation.isValidating) {
            return setError('Please wait for beta code validation to complete');
        }

        // Check if betacode is invalid
        if (betacodeValidation.isValid === false) {
            return setError(betacodeValidation.message);
        }

        if (password !== confirmPassword) {
            return setError('Passwords do not match');
        }

        if (password.length < 6) {
            return setError('Password must be at least 6 characters long');
        }

        if (!selectedGroup) {
            return setError('Group is required');
        }

        // Phone validation (updated to match new format)
        if (phone && !/^\(\d{3}\) \d{3}-\d{4}$/.test(phone)) {
            return setError('Please enter a valid phone number in format (555) 123-4567');
        }

        // Email validation
        if (!/\S+@\S+\.\S+/.test(email)) {
            return setError('Please enter a valid email address');
        }

        try {
            setError('');
            setLoading(true);

            // Prepare user profile data
            const userProfile = {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phone: phone.trim(),
                betacode: betacode.trim(),
                address: {
                    street: street.trim(),
                    city: city.trim(),
                    state: state.trim(),
                    zipCode: zipCode.trim(),
                    country: country.trim()
                },
                newGroup: subdomain === '' ? true : false
            };

            console.log('---userProfile-->',userProfile);

            await signup(email, password, selectedGroup.trim(), userProfile);

            navigate('/login');
        } catch (err) {
            let errorMessage = 'Failed to create account.';

            // Handle specific Firebase errors
            switch (err.code) {
                case 'auth/email-already-in-use':
                    errorMessage = 'An account with this email already exists.';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Please enter a valid email address.';
                    break;
                case 'auth/weak-password':
                    errorMessage = 'Password is too weak. Please choose a stronger password.';
                    break;
                default:
                    errorMessage = `Failed to create account: ${err.message}`;
            }

            setError(errorMessage);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const getEmailValidationClass = () => {
        if (!email) return '';
        if (emailValidation.isValidating) return '';
        if (emailValidation.isValid === true) return 'is-valid';
        if (emailValidation.isValid === false) return 'is-invalid';
        return '';
    };

    const getBetacodeValidationClass = () => {
        if (!betacode) return '';
        if (betacodeValidation.isValidating) return '';
        if (betacodeValidation.isValid === true) return 'is-valid';
        if (betacodeValidation.isValid === false) return 'is-invalid';
        return '';
    };

    return (
        <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
            <div className="w-100" style={{ maxWidth: '600px', borderRadius: '25px' }}>
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-center align-items-center mb-4">
                            <img
                                src={logo}
                                alt="KNOKO"
                                style={{maxWidth: '200px', height: 'auto'}}
                            />
                            <div className="mb-3 text-primary">Sign Up</div>
                        </div>
                        {/*<h2 className="text-center mb-4">Sign Up</h2>*/}
                        {error && <Alert variant="danger">{error}</Alert>}

                        <Form onSubmit={handleSubmit}>
                            {/* Personal Information Section */}
                            <div className="mb-4">
                                <h5 className="mb-3 text-primary">Personal Information</h5>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>First Name *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={firstName}
                                                onChange={(e) => setFirstName(e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Last Name *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={lastName}
                                                onChange={(e) => setLastName(e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Phone Number</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        value={phone}
                                        onChange={handlePhoneChange}
                                        placeholder="(555) 123-4567"
                                        maxLength="14"
                                    />
                                </Form.Group>
                            </div>

                            {/* Address Information Section */}
                            <div className="mb-4">
                                <h5 className="mb-3 text-primary">Address Information</h5>
                                <Form.Group className="mb-3">
                                    <Form.Label>Street Address</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={street}
                                        onChange={(e) => setStreet(e.target.value)}
                                        placeholder="123 Main Street"
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>City</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={city}
                                                onChange={(e) => setCity(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>State</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={state}
                                                onChange={(e) => setState(e.target.value)}
                                                placeholder="CA"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Zip Code</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={zipCode}
                                                onChange={(e) => setZipCode(e.target.value)}
                                                placeholder="12345"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Country</Form.Label>
                                    <Form.Select
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                    >
                                        <option value="United States">United States</option>
                                        <option value="Canada">Canada</option>
                                        <option value="United Kingdom">United Kingdom</option>
                                        <option value="Australia">Australia</option>
                                        <option value="Germany">Germany</option>
                                        <option value="France">France</option>
                                        <option value="Other">Other</option>
                                    </Form.Select>
                                </Form.Group>
                            </div>

                            {/* Account Information Section */}
                            <div className="mb-4">
                                <h5 className="mb-3 text-primary">Account Information</h5>

                                <Form.Group className="mb-3">
                                    <Form.Label>Beta Code *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={betacode}
                                        onChange={(e) => setBetacode(e.target.value.toUpperCase())}
                                        className={getBetacodeValidationClass()}
                                        placeholder="Enter your beta code"
                                        required
                                    />
                                    {betacodeValidation.message && (
                                        <Form.Text
                                            className={
                                                betacodeValidation.isValid === true ? 'text-success' :
                                                    betacodeValidation.isValid === false ? 'text-danger' :
                                                        'text-muted'
                                            }
                                        >
                                            {betacodeValidation.isValidating && (
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            )}
                                            {betacodeValidation.message}
                                        </Form.Text>
                                    )}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Email *</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={email}
                                        onChange={(e) => ( setEmail(removeDots(e.target.value)))}
                                        className={getEmailValidationClass()}
                                        required
                                    />
                                    {emailValidation.message && (
                                        <Form.Text
                                            className={
                                                emailValidation.isValid === true ? 'text-success' :
                                                    emailValidation.isValid === false ? 'text-danger' :
                                                        'text-muted'
                                            }
                                        >
                                            {emailValidation.isValidating && (
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            )}
                                            {emailValidation.message}
                                        </Form.Text>
                                    )}
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Password *</Form.Label>
                                            <Form.Control
                                                type="password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                minLength={6}
                                            />
                                            <Form.Text className="text-muted">
                                                Password must be at least 6 characters long.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Confirm Password *</Form.Label>
                                            <Form.Control
                                                type="password"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Your Group</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selectedGroup}
                                        onChange={(e) => {
                                            // Only allow alphanumeric and dash characters
                                            const sanitized = e.target.value.replace(/[^a-zA-Z0-9-]/g, '');
                                            setSelectedGroup(sanitized);
                                        }}
                                        readOnly={subdomain && subdomain.trim() !== ''}
                                        className={selectedGroup && selectedGroup.trim() !== '' ? 'bg-light' : ''}
                                        placeholder="Enter group name..."
                                        required
                                    />
                                    <Form.Text className="text-muted">
                                        No spaces or special characters
                                    </Form.Text>
                                    <br/>
                                    {groupValidation.message && (
                                        <Form.Text
                                            className={
                                                groupValidation.isValid === true ? 'text-success' :
                                                    groupValidation.isValid === false ? 'text-danger' :
                                                        'text-muted'
                                            }
                                        >
                                            {groupValidation.isValidating && (
                                                <span className="spinner-border spinner-border-sm me-2" role="status"
                                                      aria-hidden="true"></span>
                                            )}
                                            {groupValidation.message}
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </div>

                            <div className="d-flex justify-content-center align-items-center mb-3">
                                <Button
                                    disabled={loading || emailValidation.isValidating || emailValidation.isValid === false|| groupValidation.isValidating || groupValidation.isValid === false || betacodeValidation.isValidating || betacodeValidation.isValid === false}
                                    type="submit"
                                    style={{ borderRadius: '56px', width:'140px' }}
                                    size="lg"
                                >
                                    {loading ? 'Creating Account...' : 'Sign Up'}
                                </Button>
                            </div>

                            <div className="text-center mt-3">
                                <p className="mb-0">
                                    Already have an account? <Link to="/login">Log In</Link>
                                </p>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </div>
        </Container>
    );
};

export default Signup;